/**
 * RGM Judging · Unified Inbox.
 *
 * Lists BOTH:
 *   • Contest Judging assignments (Phase 1) → /judge/:contestId
 *   • Spotlight A&R Judging assignments (Phase 2) → /spotlight-ar/:spotlightId
 *
 * A small "Contest" or "Spotlight A&R" chip identifies each row.
 * Zero new nav item; ⚖️ My Judging stays the single entry point.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const _CHIP = {
  contest:      { label: "Contest",         cls: "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" },
  spotlight_ar: { label: "Spotlight A&R",  cls: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40" },
};

export default function JudgeInboxPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      const out = [];
      try {
        const r = await api.get("/judge/contests");
        for (const c of (r.data?.contests || [])) {
          out.push({
            kind: "contest",
            key: `contest:${c.contest_id}`,
            title: c.title || c.slug,
            route: `/judge/${c.contest_id}`,
            testid: `judge-inbox-row-${c.contest_id}`,
            submitted: c.submitted_count,
            total: c.assigned_count,
            closed_at: c.judging_closed_at,
            scope: c.assignment_scope,
          });
        }
      } catch { /* 404 when flag off → skip */ }
      try {
        const r = await api.get("/ar/spotlights");
        for (const s of (r.data?.spotlights || [])) {
          out.push({
            kind: "spotlight_ar",
            key: `spotlight:${s.spotlight_id}`,
            title: s.week_label || `Spotlight ${s.spotlight_id}`,
            route: `/spotlight-ar/${s.spotlight_id}`,
            testid: `judge-inbox-row-spotlight-${s.spotlight_id}`,
            submitted: s.submitted_count,
            total: s.assigned_count,
            closed_at: s.ar_review_closed_at,
            scope: s.assignment_scope,
          });
        }
      } catch { /* 404 when flag off → skip */ }
      setRows(out);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white px-4 py-6" data-testid="judge-inbox-page">
      <header className="max-w-2xl mx-auto flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-fuchsia-300 text-sm" data-testid="judge-inbox-back">← Back</button>
        <h1 className="text-lg font-black">Judging Inbox</h1>
      </header>
      <main className="max-w-2xl mx-auto mt-4 space-y-2">
        {rows === null && <p className="text-xs text-gray-500">Loading…</p>}
        {rows && rows.length === 0 && (
          <p className="text-xs text-gray-500" data-testid="judge-inbox-empty">No assignments right now.</p>
        )}
        {rows && rows.map((it) => {
          const chip = _CHIP[it.kind];
          return (
            <button
              key={it.key}
              onClick={() => navigate(it.route)}
              className="w-full text-left rounded border border-white/10 bg-white/[0.02] hover:border-fuchsia-400/40 p-3"
              data-testid={it.testid}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white truncate flex-1">{it.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${chip.cls}`} data-testid={`judge-inbox-chip-${it.kind}`}>{chip.label}</span>
                <span className="text-[10px] tabular-nums text-fuchsia-300">{it.submitted}/{it.total}</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {it.closed_at ? "Closed" : "Open"} · scope {it.scope}
              </p>
            </button>
          );
        })}
      </main>
    </div>
  );
}
